// src/components/party/AllyTargetSelector.js
import React, { useState, useRef, useEffect } from 'react';
import { usePartyVitals } from '../../context/PartyVitalsContext';

/**
 * AllyTargetSelector - Dropdown to select a party member for targeted effects
 *
 * @param {Function} onSelect - Called with the selected ally object
 * @param {Function} filter - Optional function to filter which allies are shown (ally) => boolean
 * @param {boolean} excludeSelf - If true, exclude the current character (default: true)
 * @param {string} label - Label text for the dropdown button
 * @param {string} emptyMessage - Message to show when no allies available
 * @param {string} variant - Button style variant: 'primary' | 'secondary' | 'healing' | 'support'
 * @param {boolean} disabled - If true, disable the button
 * @param {boolean} showVitals - If true, show HP/Stress info in dropdown (default: true)
 * @param {boolean} multiSelect - If true, allow selecting multiple allies
 * @param {number} maxSelections - Max allies to select in multiSelect mode
 */
const AllyTargetSelector = ({
  onSelect,
  filter = null,
  excludeSelf = true,
  label = 'Select Ally',
  emptyMessage = 'No allies in party',
  variant = 'primary',
  disabled = false,
  showVitals = true,
  multiSelect = false,
  maxSelections = 2
}) => {
  const { partyMembers, currentCharacterId, loading, isSoloPlay } = usePartyVitals();
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(multiSelect ? [] : null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get available allies
  const getAvailableAllies = () => {
    let allies = [...partyMembers];

    // Exclude self if requested
    if (excludeSelf && currentCharacterId) {
      allies = allies.filter(a => a.characterId !== currentCharacterId);
    }

    // Apply custom filter
    if (filter && typeof filter === 'function') {
      allies = allies.filter(filter);
    }

    return allies;
  };

  const availableAllies = getAvailableAllies();
  const hasAllies = availableAllies.length > 0;

  // Handle ally selection
  const handleSelect = (ally) => {
    if (multiSelect) {
      const isAlreadySelected = selected.some(s => s.characterId === ally.characterId);

      if (isAlreadySelected) {
        // Deselect
        const newSelected = selected.filter(s => s.characterId !== ally.characterId);
        setSelected(newSelected);
      } else if (selected.length < maxSelections) {
        // Add to selection
        const newSelected = [...selected, ally];
        setSelected(newSelected);

        // Auto-close and call onSelect when we've reached max
        if (newSelected.length === maxSelections) {
          setIsOpen(false);
          onSelect(newSelected);
        }
      }
    } else {
      // Single select mode
      setSelected(ally);
      setIsOpen(false);
      onSelect(ally);
    }
  };

  // Confirm multi-selection (for multiSelect mode)
  const handleConfirm = () => {
    if (multiSelect && selected.length > 0) {
      setIsOpen(false);
      onSelect(selected);
    }
  };

  // Get button variant styles
  const getVariantStyles = () => {
    // Two bases: one for compact (matches ActionRollControls buttons), one for default
    const compactBase = 'py-2 px-4 text-sm font-medium rounded-lg transition-all duration-200 border';
    const defaultBase = 'px-3 py-1.5 text-sm font-semibold rounded transition-all border';

    // Secondary uses compact base to match Experience button exactly
    const base = variant === 'secondary' ? compactBase : defaultBase;

    if (disabled || loading || !hasAllies) {
      return `${base} bg-gray-700 text-gray-500 border-gray-600 cursor-not-allowed`;
    }

    switch (variant) {
      case 'healing':
        return `${base} bg-green-900/40 text-green-300 hover:bg-green-800/60 border-green-600`;
      case 'support':
        return `${base} bg-blue-900/40 text-blue-300 hover:bg-blue-800/60 border-blue-600`;
      case 'secondary':
        // Matches Experience button in ActionRollControls exactly
        return `${base} bg-gray-800 text-gray-400 border-gray-600 hover:text-gray-200 hover:border-gray-500`;
      case 'primary':
      default:
        return `${base} bg-purple-900/40 text-purple-300 hover:bg-purple-800/60 border-purple-600`;
    }
  };

  // Loading state
  if (loading) {
    return (
      <button disabled className={getVariantStyles()}>
        Loading...
      </button>
    );
  }

  // Solo play state
  if (isSoloPlay) {
    return (
      <button disabled className={getVariantStyles()} title="No other party members">
        {emptyMessage}
      </button>
    );
  }

  // No valid allies after filtering
  if (!hasAllies) {
    return (
      <button disabled className={getVariantStyles()} title="No valid targets">
        {emptyMessage}
      </button>
    );
  }

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* Main Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={getVariantStyles()}
      >
        {multiSelect && selected.length > 0
          ? `${label} (${selected.length}/${maxSelections})`
          : label
        }
        <span className={`ml-2 inline-block transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 min-w-[200px] bg-gray-800 border border-purple-600 rounded-lg shadow-xl z-50"
        >
          <div className="max-h-60 overflow-y-auto">
            {availableAllies.map((ally) => {
              const isSelected = multiSelect
                ? selected.some(s => s.characterId === ally.characterId)
                : selected?.characterId === ally.characterId;

              return (
                <div
                  key={ally.characterId}
                  onClick={() => handleSelect(ally)}
                  className={`px-3 py-2 cursor-pointer transition-colors border-b border-gray-700 last:border-b-0 ${
                    isSelected
                      ? 'bg-purple-700/50 text-purple-200'
                      : 'text-gray-300 hover:bg-purple-900/30'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{ally.name}</span>
                    {ally.status === 'offline' && (
                      <span className="text-xs text-gray-500 ml-2">(offline)</span>
                    )}
                    {multiSelect && isSelected && (
                      <span className="text-purple-400">✓</span>
                    )}
                  </div>

                  {showVitals && (
                    <div className="flex gap-3 mt-1 text-xs">
                      <span className={ally.hp <= 2 ? 'text-red-400' : 'text-gray-400'}>
                        ❤️ {ally.hp}/{ally.maxHp}
                      </span>
                      <span className={ally.stress >= ally.maxStress - 2 ? 'text-orange-400' : 'text-gray-400'}>
                        💔 {ally.stress}/{ally.maxStress}
                      </span>
                      <span className="text-yellow-400">
                        ✨ {ally.hope}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Multi-select confirm button */}
          {multiSelect && selected.length > 0 && selected.length < maxSelections && (
            <div className="p-2 border-t border-gray-700">
              <button
                onClick={handleConfirm}
                className="w-full px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700"
              >
                Confirm ({selected.length} selected)
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AllyTargetSelector;
